import { useState, useMemo } from 'react';
import { useT } from '../i18n';

/** Absence-type label for the tooltip. `t` is passed in from the component (plain helper, not a hook). */
function absenceLabel(t: (key: string) => string, absenceType: string | null | undefined): string {
  const key = absenceType && ['vacation', 'illness', 'accident', 'measure_suspended'].includes(absenceType)
    ? absenceType
    : null;
  if (key === 'vacation') return t('chart.absence.vacation');
  if (key === 'illness') return t('chart.absence.illness');
  if (key === 'accident') return t('chart.absence.accident');
  if (key === 'measure_suspended') return t('chart.absence.measureSuspended');
  return t('chart.absence.default');
}

interface DataPoint {
  week: string;
  value: number;
  is_vacation?: boolean;
  absence_type?: string | null;
  /**
   * Marks weeks belonging to a previous mesure (vs. the current one). Prior
   * weeks render with a desaturated/lighter palette + lighter target tick.
   * Default = 'current'.
   */
  measure_phase?: 'current' | 'prior';
  /** Optional label shown above the boundary marker at the first week of a mesure. */
  measure_label?: string;
  /** Week is in the future — only planned bar rendered, realized hidden. */
  is_future?: boolean;
}

interface WeeklyChartProps {
  data: DataPoint[];
  rollingWindow: number;
  /** Unit shown in tooltip, default "h" */
  unit?: string;
  /** Format value for display — default: number with comma decimal */
  formatValue?: (v: number) => string;
  /** Reference line value (e.g. 40 for full-time hours) */
  refLine?: number;
  /** Bar fill color used when no baseline is supplied — default: var(--accent) */
  barColor?: string;
  /** Chart area height in px — default 120, use 180+ for expanded panels */
  height?: number;
  /**
   * Per-week target values (e.g. OAI-prescribed progression target).
   * Length must match `data`. `null` entries mean no target for that week.
   * When provided, bars are color-coded by actual/target ratio:
   *  - >=100%: exceeded — green base + accent overshoot stacked on top
   *  - 95–100%: reached — solid green
   *  - 80–95%: close — solid amber
   *  - <80%: below — solid red
   * Each bar with a target also gets a short horizontal tick at the
   * target level so the per-week cible is visible.
   */
  baseline?: (number | null)[];
  /**
   * Optional descriptor of the global target (e.g. "30 → 100 %") shown
   * in the legend.
   */
  baselineLabel?: string;
  /**
   * Per-week planned values (e.g. enrollment-derived planned hours/%). When
   * provided, each bar gets a thin orange bar drawn behind the realized bar.
   * Future weeks (data[i].is_future) render with a faded planned bar and no
   * realized bar. Length must match `data`.
   */
  planned?: (number | null)[];
  /** Legend label describing what the planned series represents. */
  plannedLabel?: string;
  /**
   * Per-week decomposition of the plan (past weeks only): the planned bar is
   * rendered as stacked segments attended / missed-excused / missed-unexcused /
   * unmarked (non pointé). Length matches data; null = no decomposition.
   */
  decomposition?: Array<{ attended: number; missed_excused: number; missed_unexcused: number; unmarked: number } | null>;
}

// Suffixes for the chart.month.* i18n keys, in Date#getMonth() order.
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Color buckets for actual/target ratio — see WeeklyChartProps.baseline
const BUCKETS = {
  exceed: 'var(--accent)',
  reached: 'var(--ok)',
  close: 'var(--warn)',
  below: 'var(--err)',
} as const;

/**
 * Same hue, ~45 % saturation against the surface — used for "prior mesure"
 * weeks so the timeline visually demotes them while keeping the bucket meaning.
 */
const PRIOR_BUCKETS = {
  exceed: 'color-mix(in srgb, var(--accent) 45%, var(--surface))',
  reached: 'color-mix(in srgb, var(--ok) 45%, var(--surface))',
  close: 'color-mix(in srgb, var(--warn) 45%, var(--surface))',
  below: 'color-mix(in srgb, var(--err) 45%, var(--surface))',
} as const;

function bucketColor(actual: number, target: number, phase: 'current' | 'prior' = 'current'): string {
  const set = phase === 'prior' ? PRIOR_BUCKETS : BUCKETS;
  if (target <= 0) return set.reached;
  const r = actual / target;
  if (r >= 1.0) return set.reached;
  if (r >= 0.95) return set.reached;
  if (r >= 0.80) return set.close;
  return set.below;
}

/** Convert ISO week string "IYYY-IW" to the Monday of that week */
function isoWeekToDate(isoWeek: string): Date {
  const parts = isoWeek.split('-');
  const year = Number(parts[0]);
  const week = Number(parts[1]);
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  return mon;
}

/**
 * Format the Mon–Sun span of an ISO week as a short Swiss-style range.
 * Same month:  `14.-20.4.26`
 * Cross-month: `28.4.-3.5.26`
 * Cross-year:  `29.12.25-4.1.26`
 */
function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const yy = (d: Date) => String(d.getFullYear()).slice(2);
  const dM = (d: Date) => `${d.getDate()}.${d.getMonth() + 1}`;
  if (monday.getFullYear() !== sunday.getFullYear()) {
    return `${dM(monday)}.${yy(monday)}-${dM(sunday)}.${yy(sunday)}`;
  }
  if (monday.getMonth() !== sunday.getMonth()) {
    return `${dM(monday)}.-${dM(sunday)}.${yy(sunday)}`;
  }
  return `${monday.getDate()}.-${sunday.getDate()}.${monday.getMonth() + 1}.${yy(monday)}`;
}

function defaultFormat(v: number): string {
  if (v % 1 === 0) return String(v);
  return v.toFixed(1).replace('.', ',');
}

export function WeeklyChart({
  data,
  rollingWindow,
  unit = 'h',
  formatValue = defaultFormat,
  refLine,
  barColor = 'var(--accent)',
  height = 120,
  baseline,
  baselineLabel,
  planned,
  plannedLabel,
  decomposition,
}: WeeklyChartProps) {
  const { t } = useT();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const hasBaseline = !!baseline && baseline.length === data.length && baseline.some(v => v != null);
  const hasPlanned = !!planned && planned.length === data.length && planned.some(v => v != null && v > 0);
  const hasDecomp = !!decomposition && decomposition.length === data.length && decomposition.some(d => d != null);

  const { maxVal, rollingAvg, yTicks, monthLabels } = useMemo(() => {
    let max = 0;
    for (const d of data) if (d.value > max) max = d.value;
    if (refLine && refLine > max) max = refLine;
    if (baseline) {
      for (const b of baseline) if (b != null && b > max) max = b;
    }
    if (planned) {
      for (const p of planned) if (p != null && p > max) max = p;
    }
    max = max > 0 ? Math.ceil(max / 5) * 5 : 10;

    const tickCount = height >= 150 ? 5 : 3;
    const ticks: number[] = [];
    for (let i = 0; i < tickCount; i++) {
      ticks.push(Math.round((max / (tickCount - 1)) * i));
    }

    const avg: (number | null)[] = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].is_future) {
        avg.push(null);
        continue;
      }
      if (i < rollingWindow - 1) {
        avg.push(null);
      } else {
        let sum = 0;
        for (let j = i - rollingWindow + 1; j <= i; j++) sum += data[j].value;
        avg.push(sum / rollingWindow);
      }
    }

    const labels: { index: number; label: string }[] = [];
    let prevMonth = -1;
    for (let i = 0; i < data.length; i++) {
      const d = isoWeekToDate(data[i].week);
      const monthKey = d.getMonth() + d.getFullYear() * 12;
      if (monthKey !== prevMonth) {
        labels.push({
          index: i,
          label: `${t(`chart.month.${MONTH_KEYS[d.getMonth()]}`)} ${String(d.getFullYear()).slice(2)}`,
        });
        prevMonth = monthKey;
      }
    }

    return { maxVal: max, rollingAvg: avg, yTicks: ticks, monthLabels: labels };
  }, [data, rollingWindow, refLine, height, baseline, planned, t]);

  if (data.length === 0) {
    return <div className="weekly-bars__empty">{t('chart.noData')}</div>;
  }

  // Layout
  const yAxisW = 38;
  const barW = height >= 150 ? 10 : 8;
  const gap = height >= 150 ? 3 : 2;
  const chartH = height;
  const topPad = 10;
  const labelH = 20;
  const barsW = data.length * (barW + gap) - gap;
  const totalW = yAxisW + barsW;
  const svgH = topPad + chartH + labelH;

  const refY = refLine && maxVal > 0 ? topPad + chartH - (refLine / maxVal) * chartH : null;
  const yBase = topPad + chartH;
  const yOf = (v: number): number => yBase - (v / maxVal) * chartH;

  return (
    <div className="weekly-bars">
      <svg
        width={totalW}
        height={svgH}
        viewBox={`0 0 ${totalW} ${svgH}`}
        style={{ display: 'block', maxWidth: '100%' }}
      >
        {/* Y-axis labels + grid lines */}
        {yTicks.map(tick => {
          const y = yOf(tick);
          return (
            <g key={tick}>
              <text x={yAxisW - 4} y={y + 4} textAnchor="end" fontSize="11" fill="var(--ink-3)">
                {formatValue(tick)}
              </text>
              <line x1={yAxisW} y1={y} x2={totalW} y2={y} stroke="var(--line)" strokeWidth="0.5" />
            </g>
          );
        })}

        {/* Reference line (e.g. 40h full-time) */}
        {refY != null && refLine && (
          <>
            <line
              x1={yAxisW} y1={refY} x2={totalW} y2={refY}
              stroke="var(--accent)" strokeWidth="0.75" strokeDasharray="3,3" opacity={0.5}
            />
            <text x={totalW - 2} y={refY - 3} textAnchor="end" fontSize="10" fill="var(--accent)" opacity={0.7}>
              {formatValue(refLine)} {unit}
            </text>
          </>
        )}

        {/* Mesure boundary markers — vertical dashed line + small label */}
        {data.map((d, i) => {
          if (i === 0) return null;
          if (data[i - 1].measure_phase === d.measure_phase && data[i - 1].measure_label === d.measure_label) return null;
          const x = yAxisW + i * (barW + gap) - gap / 2;
          return (
            <g key={`mb-${i}`}>
              <line
                x1={x} y1={topPad}
                x2={x} y2={topPad + chartH}
                stroke="var(--ink-3)" strokeWidth={0.75} strokeDasharray="2,3" opacity={0.55}
              />
              {d.measure_label && (
                <text
                  x={x + 2} y={topPad + 8}
                  fontSize="9" fill="var(--ink-3)"
                  opacity={0.85}
                >
                  ↳ {d.measure_label}
                </text>
              )}
            </g>
          );
        })}

        {/* Planned — future weeks: faded planned bar (derivation only).
            Past weeks WITH decomposition: stacked accounted segments
            (attended / missed excused / missed unexcused / non pointé) that
            sum to the planned total (A14b). Without decomposition: the
            classic faded planned marker behind the realized bar. */}
        {hasPlanned && data.map((d, i) => {
          const p = planned![i];
          if (p == null || p <= 0) return null;
          const x = yAxisW + i * (barW + gap);
          const isFuture = !!d.is_future;
          const dec = hasDecomp ? decomposition![i] : null;

          if (!isFuture && dec) {
            // stacked segments bottom-up: attended → missed excused → missed unexcused → unmarked
            const segs: Array<{ v: number; fill: string; hatch?: boolean }> = [
              { v: dec.attended, fill: 'var(--ok)' },
              { v: dec.missed_excused, fill: 'var(--warn)' },
              { v: dec.missed_unexcused, fill: 'var(--err)' },
              { v: dec.unmarked, fill: 'var(--ink-3)', hatch: true },
            ];
            let yCur = yBase;
            return (
              <g key={`pl-${d.week}`} pointerEvents="none">
                {segs.map((sg, k) => {
                  if (sg.v <= 0) return null;
                  const h = (sg.v / maxVal) * chartH;
                  yCur -= h;
                  return (
                    <rect key={k} x={x} y={yCur} width={barW} height={Math.max(0.5, h)} fill={sg.fill}
                          opacity={0.55} {...(sg.hatch ? { strokeDasharray: '2 2', strokeWidth: 0.5, stroke: 'var(--ink-3)' } : {})} />
                  );
                })}
                {/* planned-total ghost outline above the stack (over/under-marking visible) */}
                <rect x={x} y={yOf(p)} width={barW} height={Math.max(0, yBase - yOf(p))}
                      fill="none" stroke="var(--warn)" strokeWidth={0.75} opacity={0.8} />
              </g>
            );
          }
          return (
            <rect
              key={`pl-${d.week}`}
              x={x}
              y={yOf(p)}
              width={barW}
              height={Math.max(0, yBase - yOf(p))}
              rx={1}
              fill="var(--warn)"
              opacity={isFuture ? 0.32 : 0.55}
              pointerEvents="none"
            />
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          if (d.is_future) return null;
          const x = yAxisW + i * (barW + gap);
          const isHovered = hoveredIdx === i;
          const isVac = !!d.is_vacation;
          const isPrior = d.measure_phase === 'prior';
          const target = hasBaseline ? baseline![i] : null;
          const actual = d.value;

          // Vacation bars: keep dashed-grey rendering, ignore target colors.
          if (isVac) {
            const barH = maxVal > 0 ? (actual / maxVal) * chartH : 0;
            return (
              <rect
                key={d.week}
                x={x}
                y={yBase - barH}
                width={barW}
                height={Math.max(0, barH)}
                rx={1}
                fill={isHovered ? 'var(--ink-1)' : 'var(--ink-3)'}
                opacity={isHovered ? 0.9 : 0.35}
                strokeDasharray="2 2"
                stroke="var(--ink-3)"
                strokeWidth={0.5}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{ cursor: 'default' }}
              />
            );
          }

          // No target: legacy bar render with fade
          if (target == null) {
            const barH = maxVal > 0 ? (actual / maxVal) * chartH : 0;
            const opacity = isPrior
              ? 0.45
              : i === data.length - 1
              ? 1
              : 0.4 + (i / data.length) * 0.5;
            return (
              <rect
                key={d.week}
                x={x}
                y={yBase - barH}
                width={barW}
                height={Math.max(0, barH)}
                rx={1}
                fill={isHovered ? 'var(--ink-1)' : barColor}
                opacity={isHovered ? 0.9 : opacity}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{ cursor: 'default' }}
              />
            );
          }

          // Target present: color-coded + stacked when actual > target
          const phase = isPrior ? 'prior' : 'current';
          const yA = yOf(actual);
          const yT = yOf(target);
          const exceeds = actual > target;
          const baseColor = exceeds
            ? (isPrior ? PRIOR_BUCKETS.reached : BUCKETS.reached)
            : bucketColor(actual, target, phase);
          const exceedColor = isPrior ? PRIOR_BUCKETS.exceed : BUCKETS.exceed;
          const fade = isPrior ? 0.85 : (i === data.length - 1 ? 1 : 0.65 + (i / data.length) * 0.35);
          const tickColor = isPrior ? 'var(--ink-3)' : 'var(--ink-1)';
          const tickOpacity = isPrior ? 0.55 : 0.7;
          const lowerY = exceeds ? yT : yA;
          const lowerH = Math.max(0, yBase - lowerY);
          const upperH = exceeds ? Math.max(0, yT - yA) : 0;

          return (
            <g
              key={d.week}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: 'default' }}
            >
              {/* Lower segment: 0 → min(actual, target) */}
              <rect
                x={x}
                y={lowerY}
                width={barW}
                height={lowerH}
                rx={1}
                fill={isHovered ? 'var(--ink-1)' : baseColor}
                opacity={isHovered ? 0.9 : fade}
              />
              {/* Upper segment: target → actual (only when exceeded) */}
              {exceeds && upperH > 0 && (
                <rect
                  x={x}
                  y={yA}
                  width={barW}
                  height={upperH}
                  rx={1}
                  fill={isHovered ? 'var(--ink-1)' : exceedColor}
                  opacity={isHovered ? 0.9 : fade}
                />
              )}
              {/* Target tick across the bar (slight overhang for visibility) */}
              <line
                x1={x - 1}
                y1={yT}
                x2={x + barW + 1}
                y2={yT}
                stroke={tickColor}
                strokeWidth={isPrior ? 1 : 1.5}
                opacity={tickOpacity}
              />
            </g>
          );
        })}

        {/* Rolling average polyline */}
        {(() => {
          const points: string[] = [];
          for (let i = 0; i < rollingAvg.length; i++) {
            const val = rollingAvg[i];
            if (val == null) continue;
            const x = yAxisW + i * (barW + gap) + barW / 2;
            const y = yOf(val);
            points.push(`${x},${y}`);
          }
          if (points.length < 2) return null;
          return (
            <polyline
              points={points.join(' ')}
              fill="none" stroke="var(--ink-2)" strokeWidth="1.2"
              strokeLinecap="round" strokeLinejoin="round" opacity={0.5}
            />
          );
        })()}

        {/* Invisible hit areas — give every week (including future-planned) a hover target so the tooltip works uniformly */}
        {data.map((d, i) => {
          const x = yAxisW + i * (barW + gap);
          return (
            <rect
              key={`hit-${d.week}`}
              x={x - gap / 2}
              y={topPad}
              width={barW + gap}
              height={chartH}
              fill="transparent"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: 'default' }}
            />
          );
        })}

        {/* X-axis month labels */}
        {monthLabels.map(({ index, label }) => {
          const x = yAxisW + index * (barW + gap);
          return (
            <text
              key={`ml-${index}`}
              x={x}
              y={topPad + chartH + 14}
              textAnchor="start"
              fontSize="11"
              fill="var(--ink-3)"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {/* Legend (only when baseline is supplied) */}
      {hasBaseline && (() => {
        const anyPrior = data.some(d => d.measure_phase === 'prior');
        return (
          <div className="weekly-bars__legend">
            <span className="weekly-bars__legend-item" title={t('chart.legend.exceedTitle')}>
              <span className="weekly-bars__legend-swatch" style={{ background: BUCKETS.exceed }} />{t('chart.legend.exceed')}
            </span>
            <span className="weekly-bars__legend-item" title={t('chart.legend.reachedTitle')}>
              <span className="weekly-bars__legend-swatch" style={{ background: BUCKETS.reached }} />{t('chart.legend.reached')}
            </span>
            <span className="weekly-bars__legend-item" title={t('chart.legend.closeTitle')}>
              <span className="weekly-bars__legend-swatch" style={{ background: BUCKETS.close }} />{t('chart.legend.close')}
            </span>
            <span className="weekly-bars__legend-item" title={t('chart.legend.belowTitle')}>
              <span className="weekly-bars__legend-swatch" style={{ background: BUCKETS.below }} />{t('chart.legend.below')}
            </span>
            <span className="weekly-bars__legend-item" title={t('chart.legend.targetTitle')}>
              <span className="weekly-bars__legend-tick" />{t('chart.legend.target')}
            </span>
            {anyPrior && (
              <span
                className="weekly-bars__legend-item"
                title={t('chart.legend.priorTitle')}
              >
                <span
                  className="weekly-bars__legend-swatch"
                  style={{ background: PRIOR_BUCKETS.reached, border: '1px dashed var(--ink-3)' }}
                />{t('chart.legend.prior')}
              </span>
            )}
            {hasPlanned && (
              <span className="weekly-bars__legend-item" title={t('chart.legend.plannedTitle')}>
                <span
                  className="weekly-bars__legend-swatch"
                  style={{ background: 'var(--warn)', opacity: 0.55 }}
                />
                {plannedLabel || t('chart.legend.plannedDefault')}
              </span>
            )}
            {baselineLabel && (
              <span className="weekly-bars__legend-target">{baselineLabel}</span>
            )}
          </div>
        );
      })()}

      {/* Standalone planned legend (when no baseline) */}
      {!hasBaseline && hasPlanned && (
        <div className="weekly-bars__legend">
          <span className="weekly-bars__legend-item" title={t('chart.legend.plannedTitle')}>
            <span
              className="weekly-bars__legend-swatch"
              style={{ background: 'var(--warn)', opacity: 0.55 }}
            />
            {plannedLabel || t('chart.legend.plannedDefault')}
          </span>
        </div>
      )}

      {/* Tooltip on hover */}
      {hoveredIdx != null && (() => {
        const d = data[hoveredIdx];
        const weekDate = isoWeekToDate(d.week);
        const rangeStr = formatWeekRange(weekDate);
        const weekNum = d.week.split('-')[1];
        const target = hasBaseline ? baseline![hoveredIdx] : null;
        const delta = target != null ? d.value - target : null;
        const barX = yAxisW + hoveredIdx * (barW + gap) + barW + 6;
        const flipLeft = barX + 180 > totalW;
        const tipLeft = flipLeft
          ? yAxisW + hoveredIdx * (barW + gap) - 6
          : barX;
        return (
          <div
            className="weekly-bars__tooltip"
            style={{
              position: 'absolute',
              top: topPad + 4,
              left: tipLeft,
              transform: flipLeft ? 'translateX(-100%)' : undefined,
            }}
          >
            <div><strong>{t('chart.tooltip.week', { n: weekNum })}</strong> — {rangeStr}{d.is_vacation ? ` (${absenceLabel(t, d.absence_type)})` : ''}</div>
            {d.measure_label && (
              <div style={{ fontSize: 10, color: 'var(--ink-3)', fontStyle: d.measure_phase === 'prior' ? 'italic' : 'normal' }}>
                {d.measure_phase === 'prior' ? t('chart.tooltip.priorMeasurePrefix') : ''}{d.measure_label}
              </div>
            )}
            {!d.is_future && (
              <div className="weekly-bars__tooltip-row">
                <span className="weekly-bars__tooltip-label">{d.is_vacation ? t('chart.tooltip.estimated') : t('chart.tooltip.actual')}</span>
                <strong>{formatValue(d.value)} {unit}</strong>
              </div>
            )}
            {hasPlanned && planned![hoveredIdx] != null && planned![hoveredIdx]! > 0 && (
              <div className="weekly-bars__tooltip-row">
                <span className="weekly-bars__tooltip-label">{d.is_future ? t('chart.tooltip.plannedUpcoming') : t('chart.tooltip.planned')}</span>
                <strong style={{ color: 'var(--warn)' }}>{formatValue(planned![hoveredIdx]!)} {unit}</strong>
              </div>
            )}
            {target != null && (
              <>
                <div className="weekly-bars__tooltip-row">
                  <span className="weekly-bars__tooltip-label">{t('chart.tooltip.targetAi')}</span>
                  <strong>{formatValue(target)} {unit}</strong>
                </div>
                {delta != null && (
                  <div className="weekly-bars__tooltip-row">
                    <span className="weekly-bars__tooltip-label">{t('chart.tooltip.delta')}</span>
                    <strong style={{ color: delta >= 0 ? 'var(--ok)' : 'var(--err)' }}>
                      {delta >= 0 ? '+' : ''}{formatValue(delta)} {unit}
                    </strong>
                  </div>
                )}
              </>
            )}
            {rollingAvg[hoveredIdx] != null && (
              <div className="weekly-bars__tooltip-row">
                <span className="weekly-bars__tooltip-label">{t('chart.tooltip.rollingAvg', { n: rollingWindow })}</span>
                <strong>{formatValue(rollingAvg[hoveredIdx]!)} {unit}</strong>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
